/**
 * A-01 · Siluetas de carga.
 *
 * Piezas grises con la franja de brillo del recetario. La idea es que la
 * pantalla conserve su forma mientras llegan los datos: si el esqueleto no se
 * parece al contenido, al llegar los datos todo brinca y se pierde el sentido.
 */

/** Una barra. El ancho se pasa con clases de Tailwind. */
export const EsqueletoLinea = ({ className = 'w-full' }: { className?: string }) => (
  <div className={`sk-pieza h-3 ${className}`} />
)

/** Un bloque cuadrado, para miniaturas y avatares. */
export const EsqueletoBloque = ({ className = 'w-10 h-10' }: { className?: string }) => (
  <div className={`sk-pieza ${className}`} />
)

/**
 * Silueta de una tabla. `columnas` marca cuántas celdas por renglón; los anchos
 * se alternan para que no se vea un bloque perfecto, que delata la plantilla.
 */
export const EsqueletoTabla = ({
  filas = 6,
  columnas = 5,
}: {
  filas?: number
  columnas?: number
}) => {
  const anchos = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-1/3', 'w-3/5']

  return (
    <div className="divide-y divide-[#E4E4E4]" aria-hidden="true">
      {Array.from({ length: filas }).map((_, fila) => (
        <div key={fila} className="flex items-center gap-6 px-6 py-4">
          {Array.from({ length: columnas }).map((_, columna) => (
            <div key={columna} className="flex-1">
              <EsqueletoLinea className={anchos[(fila + columna) % anchos.length]} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/** Silueta de una rejilla de tarjetas con miniatura, como el historial. */
export const EsqueletoTarjetas = ({
  cuantas = 6,
  columnas = 'sm:grid-cols-2 lg:grid-cols-3',
}: {
  cuantas?: number
  columnas?: string
}) => (
  <div className={`grid grid-cols-1 ${columnas} gap-4`} aria-hidden="true">
    {Array.from({ length: cuantas }).map((_, i) => (
      <div
        key={i}
        className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-4"
        style={{ border: '1px solid #E0E0E0' }}
      >
        <EsqueletoBloque className="w-[76px] h-[76px] rounded-lg" />
        <div className="flex-1 space-y-2">
          <EsqueletoLinea className="w-2/3" />
          <EsqueletoLinea className="w-1/2" />
          <EsqueletoLinea className="w-1/3" />
        </div>
      </div>
    ))}
  </div>
)

/**
 * Silueta de una pantalla de detalle: la tarjeta con su encabezado y la rejilla
 * de campos. `campos` es cuántos pares etiqueta/valor se dibujan.
 */
export const EsqueletoDetalle = ({ campos = 9 }: { campos?: number }) => (
  <div
    className="bg-white rounded-xl shadow-sm border border-[#E0E0E0]"
    aria-hidden="true"
  >
    <div className="px-6 py-4 border-b border-[#E0E0E0]">
      <EsqueletoLinea className="w-48" />
    </div>
    <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: campos }).map((_, i) => (
        <div key={i} className="space-y-2">
          <EsqueletoLinea className="w-24" />
          <EsqueletoLinea className="w-2/3" />
        </div>
      ))}
    </div>
  </div>
)

/**
 * Silueta de una rejilla de tarjetas de texto: encabezado, título, un par de
 * etiquetas y la parrilla de campos. Es la forma de las solicitudes de personal
 * y de las órdenes de compra, que no llevan miniatura.
 */
export const EsqueletoTarjetasTexto = ({
  cuantas = 6,
  columnas = 'md:grid-cols-2 xl:grid-cols-3',
  campos = 4,
}: {
  cuantas?: number
  columnas?: string
  campos?: number
}) => (
  <div className={`grid grid-cols-1 ${columnas} gap-4 sm:gap-6`} aria-hidden="true">
    {Array.from({ length: cuantas }).map((_, i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <EsqueletoLinea className="w-16" />
        <EsqueletoLinea className="w-3/5 h-4" />
        <EsqueletoLinea className="w-2/5" />
        <div className="flex gap-2">
          <EsqueletoLinea className="w-16" />
          <EsqueletoLinea className="w-24" />
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
          {Array.from({ length: campos }).map((_, j) => (
            <EsqueletoLinea key={j} className={j % 2 === 0 ? 'w-2/3' : 'w-1/2'} />
          ))}
        </div>
      </div>
    ))}
  </div>
)
